// import node module libraries
import { Card, Form, Button, Row, Col, Badge } from "react-bootstrap";

// import custom components
import GKTagsInput from "components/elements/tags/GKTagsInput";

// date-range css
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { LANGUAGES } from "utils/UtilData";
import { Editor } from '@tinymce/tinymce-react';

const BasicInformation = (props) => {
  const { next, handleChange, data } = props;

  const CoursesLevel = [
    { value: "Easy", label: "Easy" },
    { value: "Medium", label: "Medium" },
    { value: "Hard", label: "Hard" },
  ];

  const TAGS = [
    { value: "Bootcamp" },
    { value: "Master Class" },
    { value: "For Parents" },
    { value: "For Healthcare Workers" },
  ];

  return (
    <Form>
      {/* Main Card */}
      <Card className="course-form-card mb-4">
        <Card.Header className="course-form-header">
          <div className="d-flex align-items-center">
            <div className="header-icon me-3">
              <i className="fe fe-info"></i>
            </div>
            <div>
              <h4 className="mb-1">Basic Information</h4>
              <p className="text-muted mb-0 small">Essential course details and description</p>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="course-form-body">
          {/* Course Title */}
          <Form.Group className="form-group-modern mb-4">
            <Form.Label className="form-label-modern">
              Course Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter an engaging course title"
              value={data?.title || ''}
              id="title"
              name="title"
              onChange={handleChange}
              className="form-control-modern"
              required
            />
            <Form.Text className="form-help-text">
              Keep it clear and descriptive (recommended: 60 characters max)
            </Form.Text>
          </Form.Group>

          {/* Course Properties Row */}
          <Row className="g-4 mb-4">
            {/* Difficulty Level */}
            <Col lg={4} md={6}>
              <Form.Group className="form-group-modern">
                <Form.Label className="form-label-modern">
                  Difficulty Level <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  id="dificultyLevel"
                  name="dificultyLevel"
                  value={data?.dificultyLevel || ''}
                  onChange={handleChange}
                  className="form-select-modern"
                  required
                >
                  <option value="">Select Level</option>
                  {CoursesLevel.map((item, index) => (
                    <option key={index} value={item.value}>{item.label}</option>
                  ))}
                </Form.Select>
                <div className="level-indicator mt-2">
                  {data?.dificultyLevel && (
                    <Badge bg="" className={`badge-level-${data.dificultyLevel.toLowerCase()}`}>
                      {data.dificultyLevel}
                    </Badge>
                  )}
                </div>
              </Form.Group>
            </Col>

            {/* Course Tag */}
            <Col lg={4} md={6}>
              <Form.Group className="form-group-modern">
                <Form.Label className="form-label-modern">
                  Course Category <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  id="tags"
                  name="tags"
                  value={data?.tags?.length !== 0 ? data.tags[0] : ""}
                  onChange={handleChange}
                  className="form-select-modern"
                  required
                >
                  <option value="">Select Category</option>
                  {TAGS.map((item, index) => (
                    <option key={index} value={item.value}>{item.value}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Language */}
            <Col lg={4} md={12}>
              <Form.Group className="form-group-modern">
                <Form.Label className="form-label-modern">
                  Course Language <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  id="language"
                  name="language"
                  value={data?.language || ""}
                  onChange={handleChange}
                  className="form-select-modern"
                  required
                >
                  <option value="">Select Language</option>
                  {LANGUAGES.map((item, index) => (
                    <option key={index} value={item.name}>{item.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Course Description */}
          <Form.Group className="form-group-modern mb-4">
            <Form.Label className="form-label-modern">
              Course Description <span className="text-danger">*</span>
            </Form.Label>
            <div className="editor-wrapper">
              <Editor
                apiKey="5hweh9wzbn7rr6kaleja3p3d8tjymzod82q5i1lcqkzb6us4"
                value={data?.description || ""}
               
                init={{
                  height: 350,
                  menubar: false,
                  toolbar:
                    'ltr rtl | undo redo | formatselect | bold italic backcolor | \
                    alignleft aligncenter alignright alignjustify | \
                    bullist numlist outdent indent | removeformat | help',
                  content_style: `
                    body { 
                      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif; 
                      font-size: 14px;
                      line-height: 1.6;
                    }
                  `
                  }}
         
                onEditorChange={(content) => handleChange({ target: { name: 'description', value: content } })}
              />
            </div>
            <Form.Text className="form-help-text">
              Provide a detailed overview of what students will learn
            </Form.Text>
          </Form.Group>

          {/* Learning Objectives */}
          <Form.Group className="form-group-modern mb-4">
            <Form.Label className="form-label-modern">
              Learning Objectives
            </Form.Label>
            <GKTagsInput
              defaulttags={data?.learnObjectives}
              onChange={(tags) => {
                handleChange({
                  target: {
                    name: "learnObjectives",
                    value: tags,
                  },
                });
              }}
            />
            <Form.Text className="form-help-text">
              <i className="fe fe-info me-1"></i>
              Press Enter after typing each objective
            </Form.Text>
          </Form.Group>

          {/* Course Requirements */}
          <Form.Group className="form-group-modern mb-4">
            <Form.Label className="form-label-modern">
              Course Requirements
            </Form.Label>
            <GKTagsInput
              defaulttags={data?.requirements}
              onChange={(tags) => {
                handleChange({
                  target: {
                    name: "requirements",
                    value: tags,
                  },
                });
              }}
            />
            <Form.Text className="form-help-text">
              <i className="fe fe-info me-1"></i>
              List prerequisites or required knowledge
            </Form.Text>
          </Form.Group>

          {/* Learning Mode */}
          <Form.Group className="form-group-modern mb-4">
            <Form.Label className="form-label-modern">
              Learning Mode <span className="text-danger">*</span>
            </Form.Label>
            <div className="learning-mode-selector">
              <Form.Check
                type="radio"
                name="learningMode"
                id="mode-online"
                value="online"
                checked={data?.learningMode === "online"}
                onChange={handleChange}
                label={
                  <div className="mode-option">
                    <i className="fe fe-monitor me-2"></i>
                    <div>
                      <strong>Online</strong>
                      <small className="d-block text-muted">Remote learning via internet</small>
                    </div>
                  </div>
                }
                className="mode-radio"
              />
              <Form.Check
                type="radio"
                name="learningMode"
                id="mode-physical"
                value="physical"
                checked={data?.learningMode === "physical"}
                onChange={handleChange}
                label={
                  <div className="mode-option">
                    <i className="fe fe-map-pin me-2"></i>
                    <div>
                      <strong>Physical</strong>
                      <small className="d-block text-muted">In-person attendance required</small>
                    </div>
                  </div>
                }
                className="mode-radio"
              />
              <Form.Check
                type="radio"
                name="learningMode"
                id="mode-hybrid"
                value="hybrid"
                checked={data?.learningMode === "hybrid"}
                onChange={handleChange}
                label={
                  <div className="mode-option">
                    <i className="fe fe-shuffle me-2"></i>
                    <div>
                      <strong>Hybrid</strong>
                      <small className="d-block text-muted">Combination of online and physical</small>
                    </div>
                  </div>
                }
                className="mode-radio"
              />
            </div>
          </Form.Group>

          {/* Pricing and Dates */}
          {(() => {
            const mode = (data?.learningMode || "").toLowerCase();
            const showOnlinePrice = mode === "online" || mode === "hybrid";
            const showPhysicalPrice = mode === "physical" || mode === "hybrid";

            return (
              <>
                {/* Pricing Row */}
                {(showOnlinePrice || showPhysicalPrice) && (
                  <Row className="g-4 mb-4">
                    {showOnlinePrice && (
                      <Col md={6}>
                        <Form.Group className="form-group-modern">
                          <Form.Label className="form-label-modern">
                            Online Course Price (USD)
                          </Form.Label>
                          <div className="price-input-wrapper">
                            {/* <span className="price-symbol">$</span> */}
                            <Form.Control
                              type="number"
                              placeholder="0.00"
                              value={data?.price || ''}
                              id="price"
                              name="price"
                              onChange={handleChange}
                              className="form-control-modern price-input"
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <Form.Text className="form-help-text">
                            Set to 0 for free courses
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    )}
                    {showPhysicalPrice && (
                      <Col md={6}>
                        <Form.Group className="form-group-modern">
                          <Form.Label className="form-label-modern">
                            Physical Attendance Price (USD)
                          </Form.Label>
                          <div className="price-input-wrapper">
                            <span className="price-symbol">$</span>
                            <Form.Control
                              type="number"
                              placeholder="0.00"
                              value={data?.physicalPrice || ''}
                              id="physicalPrice"
                              name="physicalPrice"
                              onChange={handleChange}
                              className="form-control-modern price-input"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                )}

                {/* Dates Row */}
                <Row className="g-4 mb-4">
                  <Col md={6}>
                    <Form.Group className="form-group-modern">
                      <Form.Label className="form-label-modern">
                        Course Start Date <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="date-input-wrapper">
                        <i className="fe fe-calendar input-icon"></i>
                        <Form.Control
                          type="datetime-local"
                          value={data?.courseStartDate || ''}
                          id="courseStartDate"
                          name="courseStartDate"
                          onChange={handleChange}
                          className="form-control-modern date-input"
                          required
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="form-group-modern">
                      <Form.Label className="form-label-modern">
                        Course End Date <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="date-input-wrapper">
                        <i className="fe fe-calendar input-icon"></i>
                        <Form.Control
                          type="datetime-local"
                          value={data?.courseEndDate || ''}
                          id="courseEndDate"
                          name="courseEndDate"
                          onChange={handleChange}
                          className="form-control-modern date-input"
                          required
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            );
          })()}

          {/* Virtual Link Option */}
          <Form.Group className="form-group-modern">
            <div className="virtual-link-toggle">
              <Form.Check
                type="switch"
                id="virtualLink"
                name="virtualLink"
                checked={data?.virtualLink === "True"}
                onChange={handleChange}
                label={
                  <div>
                    <strong>Create Virtual Meeting Link</strong>
                    <small className="d-block text-muted">
                      Automatically generate a virtual classroom link for this course
                    </small>
                  </div>
                }
                className="switch-modern"
              />
            </div>
          </Form.Group>
        </Card.Body>
      </Card>

      {/* Action Buttons */}
      <div className="form-actions">
        <Button variant="" className="btn-action btn-next" onClick={next}>
          Continue to Media
          <i className="fe fe-arrow-right ms-2"></i>
        </Button>
      </div>

      <style jsx>{`
        /* Card Styling */
        :global(.course-form-card) {
          border: none;
          border-radius: 16px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
          overflow: hidden;
        }

        :global(.course-form-header) {
          background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%);
          color: white;
          padding: 2rem;
          border: none;
        }

        .header-icon {
          width: 48px;
          height: 48px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }

        :global(.course-form-header) h4 {
          color: white;
          margin: 0;
          font-weight: 700;
        }

        :global(.course-form-body) {
          padding: 2rem;
        }

        /* Form Groups */
        .form-group-modern {
          margin-bottom: 1.5rem;
        }

        :global(.form-label-modern) {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
          font-size: 0.9375rem;
        }

        :global(.form-control-modern),
        :global(.form-select-modern) {
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          padding: 0.75rem 1rem;
          font-size: 0.9375rem;
          transition: all 0.3s ease;
        }

        :global(.form-control-modern:focus),
        :global(.form-select-modern:focus) {
          border-color: #F68521;
          box-shadow: 0 0 0 3px rgba(246, 133, 33, 0.1);
        }

        :global(.form-help-text) {
          font-size: 0.8125rem;
          color: #64748b;
          margin-top: 0.5rem;
          display: block;
        }

        /* Level Indicator */
        .level-indicator {
          display: flex;
          gap: 0.5rem;
        }

        :global(.badge-level-easy) {
          background: rgba(112, 165, 83, 0.15) !important;
          color: #70a553 !important;
          padding: 6px 12px;
          border-radius: 6px;
          font-weight: 600;
        }

        :global(.badge-level-medium) {
          background: rgba(246, 133, 33, 0.15) !important;
          color: #F68521 !important;
          padding: 6px 12px;
          border-radius: 6px;
          font-weight: 600;
        }

        :global(.badge-level-hard) {
          background: rgba(220, 38, 38, 0.15) !important;
          color: #dc2626 !important;
          padding: 6px 12px;
          border-radius: 6px;
          font-weight: 600;
        }

        /* Editor Wrapper */
        .editor-wrapper {
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          overflow: hidden;
          transition: border-color 0.3s ease;
        }

        .editor-wrapper:focus-within {
          border-color: #F68521;
        }

        /* Learning Mode Selector */
        .learning-mode-selector {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        :global(.mode-radio) {
          margin: 0;
        }

        :global(.mode-radio .form-check-input) {
          margin-top: 0.25rem;
        }

        :global(.mode-radio .form-check-label) {
          cursor: pointer;
          width: 100%;
        }

        .mode-option {
          display: flex;
          align-items: flex-start;
          padding: 1rem;
          background: #f8f9fa;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          transition: all 0.3s ease;
        }

        :global(.mode-radio .form-check-input:checked) ~ .form-check-label .mode-option {
          background: rgba(246, 133, 33, 0.1);
          border-color: #F68521;
        }

        .mode-option:hover {
          border-color: #F68521;
        }

        .mode-option i {
          font-size: 1.5rem;
          color: #F68521;
        }

        /* Price Input */
        .price-input-wrapper {
          position: relative;
        }

        .price-symbol {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          font-weight: 600;
          color: #64748b;
          z-index: 10;
        }

        :global(.price-input) {
          padding-left: 2.5rem !important;
        }

        /* Date Input */
        .date-input-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          pointer-events: none;
          z-index: 10;
        }

        :global(.date-input) {
          padding-right: 2.5rem !important;
        }

        /* Virtual Link Toggle */
        .virtual-link-toggle {
          background: #f8f9fa;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          padding: 1.25rem;
        }

        :global(.switch-modern .form-check-input) {
          width: 48px;
          height: 24px;
          cursor: pointer;
        }

        :global(.switch-modern .form-check-input:checked) {
          background-color: #F68521;
          border-color: #F68521;
        }

        /* Action Buttons */
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 2rem;
        }

        :global(.btn-action) {
          padding: 0.875rem 2rem;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.9375rem;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          border: none;
        }

        :global(.btn-next) {
          background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(246, 133, 33, 0.3);
        }

        :global(.btn-next:hover) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(246, 133, 33, 0.4);
        }

        /* Responsive */
        @media (max-width: 768px) {
          :global(.course-form-header) {
            padding: 1.5rem;
          }

          :global(.course-form-body) {
            padding: 1.5rem;
          }

          .learning-mode-selector {
            grid-template-columns: 1fr;
          }

          .form-actions {
            flex-direction: column;
          }

          :global(.btn-action) {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </Form>
  );
};

export default BasicInformation;